import { useRef, useCallback, useMemo } from "react";
import { Platform, View } from "react-native";
import { Gesture } from "react-native-gesture-handler";
import { useSharedValue } from "react-native-reanimated";
import type { GridStore } from "../components/Grids/grid-store";
import { scheduleOnRN } from "react-native-worklets";

interface DragSelectionConfig {
  onSelectionChange: (selections: Array<{ day: number | string; hour: number }>) => void;
  store: GridStore;
}

interface CellInfo {
  day: number | string;
  hour: number;
  ref: View | null;
}

const LONG_PRESS_DURATION = 200; // milliseconds

export function useDragSelection({ onSelectionChange, store }: DragSelectionConfig) {
  const cellRefs = useRef<{ [key: string]: CellInfo }>({});
  const isSelectingRef = useRef<boolean | null>(null);
  const containerRef = useRef<View>(null);
  const isLongPressed = useSharedValue(false);

  // Stable ref for onSelectionChange to keep gesture callbacks stable
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  const registerCell = useCallback((day: number | string, hour: number, ref: View | null) => {
    const key = `${day}-${hour}`;
    if (ref) {
      cellRefs.current[key] = { day, hour, ref };
    } else {
      delete cellRefs.current[key];
    }
  }, []);

  const getCellAtPosition = useCallback(
    (absoluteX: number, absoluteY: number, callback: (day: number | string, hour: number) => void) => {
      const { slotsPerDay } = store.getState();
      // Try to find cell that contains the touch point
      for (const [, cellInfo] of Object.entries(cellRefs.current)) {
        if (cellInfo.ref && cellInfo.hour < slotsPerDay) {
          // We'll use measureInWindow which is more reliable
          cellInfo.ref.measureInWindow((x, y, width, height) => {
            if (absoluteX >= x && absoluteX <= x + width && absoluteY >= y && absoluteY <= y + height) {
              // Handle the cell selection
              callback(cellInfo.day, cellInfo.hour);
            }
          });
        }
      }
    },
    [store],
  );

  const handleCellSelection = useCallback(
    (day: number | string, hour: number) => {
      const key = `${day}-${hour}`;

      if (isSelectingRef.current === null) {
        // First cell determines if we're selecting or deselecting
        isSelectingRef.current = true;
      }

      // Add to dragged cells in the store — only the affected cell re-renders
      store.getState().addDraggedCell(key);
    },
    [store],
  );

  const handleSelection = useCallback(
    (absoluteX: number, absoluteY: number) => {
      const adjustedAbsoluteY = Platform.OS === "android" ? absoluteY - 45 : absoluteY;
      getCellAtPosition(absoluteX, adjustedAbsoluteY, handleCellSelection);
    },
    [getCellAtPosition, handleCellSelection],
  );

  const handleBegin = useCallback(
    (absoluteX: number, absoluteY: number) => {
      isSelectingRef.current = null;
      store.getState().startDrag();

      // Handle initial touch
      handleSelection(absoluteX, absoluteY);
    },
    [handleSelection, store],
  );

  const handleUpdate = useCallback(
    (absoluteX: number, absoluteY: number) => {
      handleSelection(absoluteX, absoluteY);
    },
    [handleSelection],
  );

  const handleFinalize = useCallback(() => {
    // Read dragged cells from the store before clearing
    const { draggedCells } = store.getState();
    const selections = Object.keys(draggedCells)
      .filter((k) => draggedCells[k])
      .map((cellKey) => {
        const [day, hour] = cellKey.split("-");
        return {
          day: isNaN(Number(day)) ? day : Number(day),
          hour: Number(hour),
        };
      });

    if (selections.length > 0) {
      onSelectionChangeRef.current(selections);
    }

    store.getState().endDrag();
    isSelectingRef.current = null;
  }, [store]);

  const longPressGesture = useMemo(
    () =>
      Gesture.LongPress()
        .minDuration(LONG_PRESS_DURATION)
        .onStart(() => {
          "worklet";
          isLongPressed.value = true;
        })
        .onFinalize(() => {
          "worklet";
          isLongPressed.value = false;
        }),
    [isLongPressed],
  );

  const panGesture = useMemo(
    () =>
      Gesture.Pan()
        .activateAfterLongPress(LONG_PRESS_DURATION)
        .onBegin((event) => {
          "worklet";
          if (isLongPressed.value) {
            scheduleOnRN(handleBegin, event.absoluteX, event.absoluteY);
          }
        })
        .onUpdate((event) => {
          "worklet";
          scheduleOnRN(handleUpdate, event.absoluteX, event.absoluteY);
        })
        .onFinalize(() => {
          "worklet";
          isLongPressed.value = false;
          scheduleOnRN(handleFinalize);
        }),
    [handleBegin, handleUpdate, handleFinalize, isLongPressed],
  );

  const composedGesture = Gesture.Simultaneous(longPressGesture, panGesture);

  return {
    panGesture: composedGesture,
    registerCell,
    containerRef,
  };
}
